import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./MessageComposer.module.scss";
import { Popover } from "react-tiny-popover";
import clsx from "clsx";
import { ChatChannel } from "../../../../models";
import { useStore } from "../../../../stores";
import { toast } from "bulma-toast";
import { useTranslation } from "react-i18next";
import locale from "../../../../constants/locale";
import useMobileResponsive from "../../../../hooks/useMobileResponsive";
import WebcamCapture from "./WebcamCapture";
import { Modal } from "../../../../components";
import { uploadFiles } from "../../../../stores/utils";

const MAX_ALLOWED_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_VIDEO_SIZE = 2 * 100 * 1024 * 1024; // 200 MB
const IMAGE_MIN_LARGE_SIZE = 5 * 1024 * 1024; // 5 MB

const ALLOWED_FILE_TYPES = [
  "audio/mpeg",
  "audio/mp3",
  "audio/ogg",
  "audio/amr",
  "image/jpeg",
  "image/png",
  "video/mp4",
  "application/pdf",
  "text/vcard",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel", // .xls files
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx files
  "application/msword",
  "emailTemplate",
  "contentTemplate",
];

export default function FileUploader({
  value,
  isLoading,
  setIsLoading,
  showRecorder,
  files,
  setCurrent,
  pastedFiles,
  setPastedFiles,
  setFiles,
  setShowRecorder,
}: {
  value?: string;
  showRecorder?: boolean;
  files?: Blob[];
  setCurrent: (current: any) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  pastedFiles?: Blob[];
  setPastedFiles: (files: Blob[]) => void;
  setFiles: (files: Blob[]) => void;
  setShowRecorder: (show: boolean) => void;
}) {
  const { t } = useTranslation();
  const isMobile = useMobileResponsive();
  const { selectedChat } = useStore("chats");
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [webcamOpen, setWebcamOpen] = useState(false);
  const [largeFileModal, setLargeFileModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const hasSizeLimit = useMemo(
    () => (blob: Blob) => {
      if (!ALLOWED_FILE_TYPES.includes(blob.type)) {
        toast({
          message: t(locale.fileHasNoSupportedFormat),
          position: "top-right",
          dismissible: true,
          type: "is-warning",
          pauseOnHover: true,
        });
        return true;
      }

      if (blob.size > MAX_ALLOWED_SIZE) {
        toast({
          message: t(locale.fileIsTooLarge, { size: `10 MB` }),
          position: "top-right",
          dismissible: true,
          type: "is-warning",
          pauseOnHover: true,
        });
        return true;
      }

      return false;
    },
    [t]
  );

  const checkFiles = useCallback(
    (files: File[]) => {
      const videoFiles = Array.from(files).filter((file) =>
        file.type.startsWith("video/")
      );
      const imageFiles = Array.from(files).filter((file) =>
        file.type.startsWith("image/")
      );

      let skipNow = false;

      if (imageFiles.length > 0) {
        for (const file of imageFiles) {
          if (file.size > MAX_ALLOWED_SIZE) {
            // For images > 10MB, show validation error
            toast({
              message: t(locale.fileIsTooLarge, { size: `10 MB` }),
              position: "top-right",
              dismissible: true,
              type: "is-warning",
              pauseOnHover: true,
            });
            skipNow = true;
            return;
          } else if (file.size > IMAGE_MIN_LARGE_SIZE) {
            // For images between 5MB and 10MB, show large file modal
            setLargeFileModal(true);
            setSelectedFile(file);
            setIsPopoverOpen(false);
            skipNow = true;
            return;
          }
        }
      }

      // Handle video files separately
      videoFiles.forEach((file) => {
        if (file.size > MAX_VIDEO_SIZE) {
          toast({
            message: t(locale.fileIsTooLarge, { size: `200 MB` }),
            position: "top-right",
            dismissible: true,
            type: "is-warning",
            pauseOnHover: true,
          });
          skipNow = true;
          return;
        } else if (file.size > MAX_ALLOWED_SIZE) {
          setLargeFileModal(true);
          setSelectedFile(file);
          setIsPopoverOpen(false);
          skipNow = true;
          return;
        }
      });

      if (skipNow) return;

      // Handle other files as before
      for (const file of files) {
        if (hasSizeLimit(file)) {
          return;
        }
      }

      setFiles(files as unknown as Blob[]);
    },
    [hasSizeLimit, setFiles, t]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { target } = event;
      const { files } = target;

      if (files) {
        checkFiles(files as unknown as File[]);
      }
      setIsPopoverOpen(false);
    },
    [checkFiles]
  );

  const onFileBrowse = useCallback(
    (e: any) => {
      if (isMobile) {
        e.preventDefault();
        setWebcamOpen(true);
      }
    },
    [isMobile]
  );

  const onUploadLargeFile = async () => {
    if (!selectedFile) return;
    setLargeFileModal(false);
    setIsLoading(true);
    try {
      const urls = await uploadFiles([selectedFile]);
      setCurrent({ text: urls.join("\n") });
    } catch (error) {
      console.error("Error uploading file:", error);
      // Handle error (e.g., show error message to user)
    } finally {
      setIsLoading(false);
      setSelectedFile(null);
    }
  };

  useEffect(() => {
    if (pastedFiles?.length) {
      const ref = pastedFiles;
      setPastedFiles([]);
      checkFiles(ref as unknown as File[]);
    }
  }, [checkFiles, pastedFiles, pastedFiles?.length, setPastedFiles]);

  return (
    <>
      <Popover
        isOpen={isPopoverOpen}
        positions={["top"]}
        containerClassName={styles.popOver}
        padding={7}
        parentElement={
          document.getElementById("section-conversation") || undefined
        }
        onClickOutside={() => setIsPopoverOpen(false)}
        content={() => (
          <div>
            <div
              className={clsx(
                styles.attachmentContainer,
                "is-flex is-flex-direction-column mb-4"
              )}
            >
              {selectedChat?.channel === ChatChannel.Instagram ? (
                <input
                  id="image-video-picker"
                  type="file"
                  className="is-hidden"
                  accept="image/jpeg,image/png"
                  multiple
                  onChange={handleFileChange}
                />
              ) : (
                <>
                  <input
                    id="image-video-picker"
                    type="file"
                    className="is-hidden"
                    accept="image/jpeg,image/png,video/mp4"
                    multiple
                    onChange={handleFileChange}
                    onClick={onFileBrowse}
                  />
                  {/* <LargeFileUpload onUploadComplete={handleLargeFileUpload} /> */}
                </>
              )}
              <label
                htmlFor="image-video-picker"
                className="button is-ghost p-0"
              >
                <span className="">
                  <img src="/assets/file-picker.svg" alt="" />
                </span>
              </label>
              {selectedChat?.channel !== ChatChannel.Instagram && (
                <>
                  <input
                    id="audio-picker"
                    type="file"
                    className="is-hidden"
                    accept=".mp3,.amr,audio/ogg"
                    multiple
                    onChange={handleFileChange}
                  />
                  <label htmlFor="audio-picker" className="button is-ghost p-0">
                    <span className="">
                      <img src="/assets/audio-picker.svg" alt="" />
                      {/* <i className="fas fa-file-audio"></i> */}
                    </span>
                  </label>
                  <button
                    className="button is-ghost p-0"
                    disabled={!(window as any).MediaRecorder}
                    onClick={() => {
                      setShowRecorder(true);
                      setIsPopoverOpen(false);
                    }}
                  >
                    <span className="">
                      <img src="/assets/mic-picker.svg" alt="" />
                      {/* <i className="fas fa-microphone-alt"></i> */}
                    </span>
                  </button>
                  <input
                    id="file-picker"
                    type="file"
                    className="is-hidden"
                    accept=".pdf,.vcf,.xls,.xlsx"
                    multiple
                    onChange={handleFileChange}
                  />
                  <label htmlFor="file-picker" className="button is-ghost p-0">
                    <span className="">
                      <img src="/assets/document-picker.svg" alt="" />
                      {/* <i className="fas fa-file-alt"></i> */}
                    </span>
                  </label>
                </>
              )}
            </div>
          </div>
        )}
      >
        <button
          className={clsx("button is-ghost is-no-box-shadow has-text-primary", {
            "is-loading": isLoading,
          })}
          disabled={!selectedChat?.isConnected || (showRecorder && !files)}
          onClick={() => {
            if (!value && !files && !showRecorder) {
              setIsPopoverOpen(!isPopoverOpen);
            } else {
              setCurrent({});
            }
          }}
        >
          <span className="icon">
            {value || files || showRecorder ? (
              <i
                className={clsx("fas", {
                  "fa-arrow-right": value || files || showRecorder,
                })}
              ></i>
            ) : (
              <img src="/assets/attachment-add.svg" alt="attach" />
            )}
          </span>
        </button>
      </Popover>
      {webcamOpen && (
        <WebcamCapture
          onClose={() => setWebcamOpen(false)}
          onChange={handleFileChange}
        />
      )}
      {largeFileModal && (
        <Modal
          isOpen
          onClose={() => setLargeFileModal(false)}
          title={t(locale.largeFileUpload)}
        >
          <div>
            <p>{t(locale.fileLargerThanAllowedSize, { size: "10MB" })}</p>
            <div className="is-flex is-justify-content-flex-end mt-4">
              <button
                className="button is-light mr-2"
                onClick={() => setLargeFileModal(false)}
              >
                {t(locale.cancel)}
              </button>
              <button className="button is-primary" onClick={onUploadLargeFile}>
                {t(locale.continue)}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
